import { useLayoutEffect, useRef, useState } from 'react';
import {
  UseHorizontalOverflowParams,
  UseHorizontalOverflowResult,
} from './useHorizontalOverflow.types';
import { computeVisibleCount } from './useHorizontalOverflow.utils';

export function useHorizontalOverflow({
  itemCount,
  recalculationKey,
  gap,
  trailer,
  measureFrom = 'self',
  minVisible = 0,
  extraReservePx = 0,
  availableWidthRatio = 1,
}: UseHorizontalOverflowParams): UseHorizontalOverflowResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  useLayoutEffect(() => {
    const measure = measureRef.current;
    const container =
      measureFrom === 'parent' ? containerRef.current?.parentElement : containerRef.current;

    if (!measure || !container) {
      return;
    }

    const compute = () => {
      const styles = getComputedStyle(container);
      const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const availableWidth =
        (container.clientWidth - paddingX) * availableWidthRatio - extraReservePx;

      const children = Array.from(measure.children) as HTMLElement[];
      const widths = children.slice(0, itemCount).map((child) => child.offsetWidth);

      const reserve =
        trailer.mode === 'measured'
          ? (children[itemCount]?.offsetWidth ?? 0) + gap
          : trailer.reservePx;

      setVisibleCount(computeVisibleCount({ widths, gap, availableWidth, reserve, minVisible }));
    };

    compute();

    const observer = new ResizeObserver(compute);
    observer.observe(container);

    return () => observer.disconnect();
  }, [
    itemCount,
    recalculationKey,
    gap,
    measureFrom,
    minVisible,
    extraReservePx,
    availableWidthRatio,
    trailer,
  ]);

  return { containerRef, measureRef, visibleCount, hiddenCount: itemCount - visibleCount };
}
